import React from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import useOrganisation from "../../../../hooks/super/useOrganisation";

const TOKENS = {
  primary: "#6371F7",
  primaryHover: "#0244BE",
  danger: "#FB3748",
  dangerHover: "#D00416",
  dangerBg: "#FEECEE",
  success: "#1FC16B",
  fg1: "#292929",
  fg2: "#7B7B7B",
  fg3: "#959595",
  border: "#D4D4D4",
  borderSubtle: "rgba(0,0,0,0.08)",
  bgSurface: "#FFFFFF",
  bgSunken: "#F7F7F9",
  bgOverlay: "rgba(17,17,17,0.5)",
  shadowL: "0 12px 32px rgba(17,17,17,0.08), 0 4px 8px rgba(17,17,17,0.04)",
  shadowFocus: "0 0 0 3px rgba(99,113,247,0.22)",
};

const MONO_COLORS = [
  ["#EBEDF5", "#454FAD"],
  ["#EDF3FE", "#0255ED"],
  ["#E8FBEF", "#0f7a42"],
  ["#FFF6D0", "#916C19"],
  ["#FEECEE", "#A11228"],
  ["#F3E7FF", "#7A3FB8"],
  ["#E6F7F6", "#0E7C75"],
  ["#FDEFE6", "#B5500F"],
];

function monoFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return MONO_COLORS[h % MONO_COLORS.length];
}

function initials(name = "") {
  const parts = String(name).replace(/[^A-Za-z0-9 ]/g, "").trim().split(/\s+/);
  if (!parts[0]) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((parts[0][0] || "") + (parts[1][0] || "")).toUpperCase();
}

const OrgRow = ({ org }) => {
  const [bg, fg] = monoFor(org.title || "");
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        px: "12px",
        py: "10px",
        "&:not(:last-of-type)": {
          borderBottom: `1px solid ${TOKENS.borderSubtle}`,
        },
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          flex: "0 0 auto",
          borderRadius: "8px",
          background: bg,
          color: fg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 0.4,
        }}
      >
        {initials(org.title || "")}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            color: TOKENS.fg1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {org.title || "(untitled)"}
        </Typography>
        <Typography
          sx={{
            fontFamily: '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            fontSize: 11,
            color: TOKENS.fg3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {org.id}
        </Typography>
      </Box>
    </Stack>
  );
};

function DeleteOrganisationModal({ open, handleClose, orgID, orgName, removeFlag }) {
  const [deleting, setDeleting] = React.useState(false);
  const { deleteOrganisation } = useOrganisation();

  const orgs = Array.isArray(orgName) ? orgName : [];
  const count = orgs.length;
  const action = removeFlag ? "Remove" : "Delete";

  const title =
    count > 1
      ? `${action} ${count} organisations?`
      : `${action} organisation?`;

  const body = removeFlag
    ? "This permanently deletes this organisation and all associated sites and users. This action cannot be undone."
    : "This soft-deletes this organisation and suspends all associated sites and users. You can restore it later from the deleted-organisations view.";

  const onClose = () => {
    if (deleting) return;
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          maxWidth: "92vw",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: TOKENS.shadowL,
          m: 2,
        },
      }}
      BackdropProps={{ sx: { backgroundColor: TOKENS.bgOverlay } }}
    >
      <Box sx={{ p: "24px" }}>
        {/* Header row: icon + title + close */}
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 42,
              height: 42,
              flex: "0 0 auto",
              borderRadius: "11px",
              backgroundColor: TOKENS.dangerBg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 20, color: TOKENS.danger }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: TOKENS.fg1,
                  letterSpacing: -0.1,
                }}
              >
                {title}
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                disabled={deleting}
                sx={{
                  mt: "-6px",
                  mr: "-6px",
                  color: TOKENS.fg2,
                  "&:hover": { color: TOKENS.fg1 },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 13.5,
                color: TOKENS.fg2,
                lineHeight: 1.5,
              }}
            >
              {body}
            </Typography>
          </Box>
        </Stack>

        {/* Org list */}
        {orgs.length > 0 && (
          <Box
            sx={{
              mt: 2,
              border: `1px solid ${TOKENS.borderSubtle}`,
              borderRadius: "10px",
              maxHeight: 168,
              overflow: "auto",
              backgroundColor: TOKENS.bgSurface,
            }}
          >
            {orgs.map((org) => (
              <OrgRow key={org.id} org={org} />
            ))}
          </Box>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 3 }}
        >
          <Button
            type="button"
            onClick={onClose}
            disableElevation
            disabled={deleting}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "8px",
              border: `1px solid ${TOKENS.border}`,
              backgroundColor: "transparent",
              color: TOKENS.fg1,
              fontSize: 13,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { backgroundColor: TOKENS.bgSunken, borderColor: TOKENS.fg3 },
            }}
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disableElevation
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);
              try {
                await deleteOrganisation(orgID, removeFlag);
              } finally {
                setDeleting(false);
                handleClose();
              }
            }}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "8px",
              backgroundColor: TOKENS.danger,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { backgroundColor: TOKENS.dangerHover },
              "&.Mui-disabled": {
                backgroundColor: TOKENS.danger,
                color: "#fff",
                opacity: 0.6,
                cursor: "default",
              },
            }}
            startIcon={
              deleting ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              )
            }
          >
            {deleting
              ? `${action.replace(/e$/, "")}ing…`
              : count > 1
              ? `${action} ${count}`
              : action}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

export default DeleteOrganisationModal;
